import { Box, ButtonBase, Card, Collapse, Divider, IconButton, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { CountBadge, Dot, Flex, HStack, colors } from "@zfp-akademie-a-s/zfp-design";
import { ChevronDownIcon, InfoIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type ReactNode, useState } from "react";
import { type NsureSelectionItem, NsureSelectionItemType } from "./InvestmentsNsureSelectionContext";
import { InvestmentsNsureSelectionHeader } from "./InvestmentsNsureSelectionHeader";
import { useInstrumentTypeLabel } from "./useInstrumentTypeLabel";
import { formatPercent, formatSrriSri } from "./utils";

type Props = {
  readonly selection: NsureSelectionItem;
  // the blocks after the composition, e.g. the evaluation
  readonly children?: ReactNode;
};

type CompositionRowProps = {
  readonly name: string;
  readonly weight: number | null;
  readonly meta?: ReactNode;
  readonly action?: ReactNode;
};

const CompositionRow = ({ name, weight, meta, action }: CompositionRowProps) => (
  <Flex px={Spacing.XL} py={Spacing.M} alignItems="center" gap={Spacing.M}>
    <Stack flex={1} minWidth={0} spacing={Spacing.XS}>
      <Typography
        variant="body2"
        title={name}
        sx={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden", overflowWrap: "anywhere" }}
      >
        {name}
      </Typography>
      {meta && (
        <HStack alignItems="center" gap={Spacing.M}>
          {meta}
        </HStack>
      )}
    </Stack>

    <HStack alignItems="center" gap={Spacing.S} flexShrink={0}>
      <LinearProgress
        variant="determinate"
        value={Math.min(Math.max(weight ?? 0, 0), 100)}
        sx={{
          width: 40,
          height: 4,
          borderRadius: 2,
          bgcolor: colors.backgroundSecondary,
          "& .MuiLinearProgress-bar": { borderRadius: 2 },
        }}
      />
      {/* at least as wide as "100 %", so the bars line up, and never wrapped, e.g. "99,5 %" */}
      <Typography variant="body2" fontWeight={600} minWidth={44} textAlign="right" whiteSpace="nowrap">
        {formatPercent(weight)}
      </Typography>
      {action}
    </HStack>
  </Flex>
);

export const InvestmentsNsureSelectionCard = ({ selection, children }: Props) => {
  const { t } = useResource();
  const getInstrumentTypeLabel = useInstrumentTypeLabel();
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const isPortfolio = selection.type === NsureSelectionItemType.Portfolio;
  const compositionCount = isPortfolio ? (selection.strategyInstruments?.length ?? 0) : (selection.instrumentRelations?.length ?? 0);

  // One card, the brand-tinted highlight one, with 16px corners: its blocks are split by its tinted dividers alone
  return (
    <Card variant="highlight" sx={{ p: Spacing.XL + Spacing.S, borderRadius: Radius.XL }}>
      <Stack spacing={Spacing.L} divider={<Divider />}>
        <InvestmentsNsureSelectionHeader selection={selection} />

        {compositionCount > 0 && (
          // the row runs out to the card's edges, past its padding
          <Box mx={-Spacing.M}>
            <ButtonBase
              onClick={() => setDetailsExpanded((prev) => !prev)}
              aria-expanded={detailsExpanded}
              aria-label={t(
                detailsExpanded
                  ? Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentDetail.CollapseComposition
                  : Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentDetail.ExpandComposition,
              )}
              sx={{
                width: "100%",
                minHeight: 44,
                justifyContent: "space-between",
                px: Spacing.M,
                borderRadius: 2,
                "&:hover": { bgcolor: colors.brandLight },
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentDetail.StrategyComposition)}
              </Typography>
              <HStack alignItems="center" gap={Spacing.M}>
                <CountBadge count={compositionCount} isActive />
                <ChevronDownIcon
                  fontSize="small"
                  sx={{
                    color: "text.secondary",
                    transition: "transform 0.2s",
                    transform: detailsExpanded ? "rotate(180deg)" : "none",
                  }}
                />
              </HStack>
            </ButtonBase>

            <Collapse in={detailsExpanded}>
              <Divider />
              <Stack pb={Spacing.M} divider={<Divider />}>
                {isPortfolio
                  ? selection.strategyInstruments?.map((instrument) => (
                      <CompositionRow key={instrument.id} name={instrument.name ?? "—"} weight={instrument.participationInPercentage} />
                    ))
                  : selection.instrumentRelations?.map((rel) => (
                      <CompositionRow
                        key={rel.instrumentRelationID}
                        name={rel.instrument?.name ?? "—"}
                        weight={rel.weight ?? null}
                        meta={
                          <>
                            <Typography variant="caption" color="text.secondary">
                              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.SrriSri)}{" "}
                              {formatSrriSri(rel.instrument?.srri ?? null, rel.instrument?.sri ?? null)}
                            </Typography>
                            {rel.instrument?.instrumentType && (
                              <>
                                <Dot />
                                <Typography variant="caption" color="text.secondary">
                                  {getInstrumentTypeLabel(rel.instrument.instrumentType)}
                                </Typography>
                              </>
                            )}
                          </>
                        }
                        action={
                          <Tooltip
                            arrow
                            title={
                              <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: Spacing.XS, columnGap: Spacing.L }}>
                                <Typography variant="caption" color="grey.100">
                                  {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Isin)}
                                </Typography>
                                <Typography variant="caption" color="grey.50">
                                  {rel.instrument?.isin ?? "—"}
                                </Typography>
                                <Typography variant="caption" color="grey.100">
                                  {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Symbol)}
                                </Typography>
                                <Typography variant="caption" color="grey.50">
                                  {rel.instrument?.ticker ?? "—"}
                                </Typography>
                              </Box>
                            }
                          >
                            <IconButton
                              size="small"
                              sx={{ flexShrink: 0, p: Spacing.XS }}
                              aria-label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.InstrumentDetail.InstrumentInfo)}
                            >
                              <InfoIcon fontSize="small" sx={{ color: "text.secondary" }} />
                            </IconButton>
                          </Tooltip>
                        }
                      />
                    ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {children}
      </Stack>
    </Card>
  );
};
